import React from 'react';
import PropTypes from 'prop-types';
import ProfileMetricCard from '../../CandidateComponents/Shards/ProfileMetricCard';
import AdditionalMetricCard from '../../CandidateComponents/Shards/AdditionalMetricCard';
import StrengthsConcernsCard from '../../CandidateComponents/Shards/StrengthsConcernsCard';
import HardSkillsSection from '../../CandidateComponents/Shards/HardSkillsSection';
import SoftSkillsSection from '../../CandidateComponents/Shards/SoftSkillsSection';
import SpokenLanguageSummary from '../../CandidateComponents/Shards/SpokenLanguageSummary';
import AreasOfExpertise from '../../CandidateComponents/Shards/AreasOfExpertise';
import CustomTooltip from '../../Utils/CustomTooltip';

/**
 * TalentReportContent Component
 * Displays talent-specific report content using the same components as TalentReport.js
 */
const TalentReportContent = ({ talent }) => {
  // Check if we have talent report data
  const hasTalentReportData = !!talent?.talent_report_json;

  if (!hasTalentReportData) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500 py-8">
          <div className="bg-blue-50 rounded-full p-6 mb-4 inline-block">
            <svg className="w-16 h-16 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No Talent Report Data Available</h3>
          <p className="text-gray-600">Talent report data is not available for this talent.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Additional Key Metrics Section */}
      {(talent?.talent_report_json?.["AI Proficiency"]?.value || talent?.talent_report_json?.["Multidisciplinary Expertise"]?.value || talent?.talent_report_json?.["Security Awareness"]?.value) && (
        <div className="border border-gray-200 rounded-lg p-6 bg-white">
          <h4 className="text-xl font-semibold text-gray-800 mb-4">Additional Key Metrics</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* AI Proficiency */}
            {talent?.talent_report_json?.["AI Proficiency"]?.value !== undefined && (
              <AdditionalMetricCard 
                title="AI Proficiency"
                value={talent.talent_report_json["AI Proficiency"].value}
                rationale={talent.talent_report_json["AI Proficiency"].rationale}
                source={talent.talent_report_json["AI Proficiency"].source}
              />
            )}
            
            {/* Multidisciplinary Expertise */}
            {talent?.talent_report_json?.["Multidisciplinary Expertise"]?.value !== undefined && (
              <AdditionalMetricCard 
                title="Multidisciplinary Expertise"
                value={talent.talent_report_json["Multidisciplinary Expertise"].value}
                rationale={talent.talent_report_json["Multidisciplinary Expertise"].rationale}
                source={talent.talent_report_json["Multidisciplinary Expertise"].source}
              />
            )}
            
            {/* Security Awareness */}
            {talent?.talent_report_json?.["Security Awareness"]?.value !== undefined && (
              <AdditionalMetricCard 
                title="Security Awareness"
                value={talent.talent_report_json["Security Awareness"].value}
                rationale={talent.talent_report_json["Security Awareness"].rationale}
                source={talent.talent_report_json["Security Awareness"].source}
              />
            )}
          </div>
        </div>
      )}

      {/* Strengths and Concerns Section */}
      {(talent?.talent_report_json?.strengths?.items || talent?.talent_report_json?.concerns?.items) && (
        <StrengthsConcernsCard 
          strengths={talent?.talent_report_json?.strengths || {}}
          concerns={talent?.talent_report_json?.concerns || {}}
        />
      )}

      {/* Hard Skills Section */}
      {(talent?.eval_hardskills_json?.hardSkills || talent?.talent_report_json?.hardSkills) && (
        <HardSkillsSection 
          hardSkills={{ 
            value: talent?.eval_hardskills_json?.hardSkills?.items || talent?.talent_report_json?.hardSkills?.items, 
            source: "Talent Evaluation" 
          }}
          radarChartMode="all"
        />
      )}

      {/* Soft Skills Section */}
      {talent?.eval_softskills_json && (
        <SoftSkillsSection softSkills={{ value: talent.eval_softskills_json, source: "Resume, Vetting" }} />
      )}

      {/* Spoken Languages Section */}
      {talent?.eval_language_json && talent.eval_language_json.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-6 bg-white">
          <SpokenLanguageSummary spokenLanguageData={talent.eval_language_json} />
        </div>
      )}

      {/* Areas of Expertise Section */}
      {talent?.eval_hardskills_json?.expertise && (
        <AreasOfExpertise expertise={talent.eval_hardskills_json.expertise} />
      )}
    </div>
  );
};

TalentReportContent.propTypes = {
  talent: PropTypes.object.isRequired
};

export default TalentReportContent;
